import Vue from 'vue'
import Router from 'vue-router'
import Meta from 'vue-meta'

import {
  isAuthenticated,
  hasPermissions
} from './services/auth'
import { i18next } from './i18n'

import Unauthorized401 from './pages/Unauthorized401.vue'
import Forbidden403 from './pages/Forbidden403.vue'
import PageNotFound404 from './pages/PageNotFound404.vue'

import routesPosts from './routes/posts'
import routesPortals from './routes/portals'
import routesPromptLibraries from './routes/promptLibraries'
import routesContacts from './routes/contacts'
import routesUsers from './routes/users'
import routesSocialPosts from './routes/socialNetworkPosts'
import routesSelectionVo from './routes/selectionVo'
import routesAnnoncesCommerciaux from './routes/annoncesCommerciaux'
import store from './store'
import { signOut } from '@/services/auth'

Vue.use(Router)
Vue.use(Meta)

const router = new Router({
  mode: 'history',
  base: processEnvConfig.BASE_URL,
  routes: [
    {
      path: '/',
      component: () =>
        import(/* webpackChunkName: "dashboard" */ './layouts/UserLayout.vue'),
      meta: {
        title: i18next.t('routes.homeTitle'),
        // true = only for authenticated users
        // false = only for guests users
        // <string> = only for authenticated users with permission X
        // <string[]> = only for authenticated users with at least one of the permissions
        auth: 'ROLE_USER'
      },
      children: [
        {
          path: '/',
          name: 'dashboard',
          meta: {
            title: i18next.t('layouts.dashboard')
          },
          component: () =>
            import(/* webpackChunkName: "dashboard" */ './pages/Dashboard.vue')
        },
        routesAnnoncesCommerciaux,
        routesContacts,
        routesPosts,
        routesPortals,
        routesPromptLibraries,
        routesSelectionVo,
        routesSocialPosts,
        routesUsers,
        {
          path: '/calendrier',
          name: 'calendrier',
          meta: {
            title: i18next.t('planning.calendar')
          },
          component: () =>
            import(/* webpackChunkName: "calendrier" */ '@/pages/calendar/Calendar.vue')
        },
        {
          path: '/calendrier/details/:date',
          name: 'calendrier-details',
          props: true,
          meta: {
            auth: 'ROLE_USER',
            title: i18next.t('planning.postsList')
          },
          component: () =>
            import(/* webpackChunkName: "calendrier-details" */ '@/pages/calendar/CalendarDetails.vue')
        },
        {
          path: '/profile',
          name: 'profile',
          meta: {
            title: i18next.t('routes.myAccountTitle')
          },
          component: () => import(/* webpackChunkName: "profile" */ './pages/Profile.vue')
        },
        {
          path: '/exports',
          name: 'exports',
          meta: {
            title: i18next.t('routes.exports')
          },
          component: () => import(/* webpackChunkName: "exports" */ './pages/Exports.vue')
        },
        {
          path: '/presenceLocale',
          name: 'presence',
          meta: {
            title: i18next.t('routes.localPresenceTitle')
          },
          component: () =>
            import(/* webpackChunkName: "presence" */ './pages/presenceLocale/Presence.vue')
        },
        {
          path: '/socialNetworks',
          name: 'social-networks',
          meta: {
            title: i18next.t('routes.socialNetworks')
          },
          component: () =>
            import(/* webpackChunkName: "socialNetworks" */ './pages/socialNetworks/SocialNetworks.vue')
        },
        {
          path: '/socialNetworkPosts',
          name: 'socialNetworkPosts',
          meta: {
            title: i18next.t('socialNetworkPosts.title')
          },
          component: () =>
            import(/* webpackChunkName: "presence" */ './pages/socialNetworks/SocialNetworkPosts.vue')
        },
        {
          path: '/new_socialnetwork_post',
          name: 'new-social-post',
          meta: {
            title: i18next.t('posts.list.createPost')
          },
          component: () =>
            import(/* webpackChunkName: "presence" */ './pages/socialNetworks/SocialPostsNew.vue')
        },
        {
          path: '/new_socialnetwork_post/new-editorial',
          name: 'new-post-editorial',
          meta: {
            title: i18next.t('routes.managePosts')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/socialNetworks/SocialPostEditorialCreate.vue')
        },
        {
          path: '/new_socialnetwork_post/edit-editorial/:id',
          name: 'edit-post-editorial',
          meta: {
            title: i18next.t('routes.adsListTitle')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/socialNetworks/SocialPostEditorialCreate.vue')
        },
        {
          path: '/socialnetwork_post/view-editorial/:id',
          name: 'view-post-editorial',
          meta: {
            title: i18next.t('routes.adsListTitle')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/socialNetworks/SocialPostEditorialCreate.vue')
        },
        {
          path: '/socialpostsedito/modeles',
          name: 'post-editorial-modeles',
          meta: {
            title: i18next.t('routes.modelsEditoLibrary')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/socialNetworks/SocialPostsModeles.vue')
        },
        {
          path: '/socialpostsedito/modeles/edit/:id',
          name: 'post-editorial-modeles-edit',
          meta: {
            title: i18next.t('routes.socialPosts.models.edit')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/socialNetworks/SocialPostEditorialCreate.vue')
        },
        {
          path: '/socialpostsedito/modeles/create',
          name: 'post-editorial-modeles-create',
          meta: {
            title: i18next.t('routes.socialPosts.models.create')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/socialNetworks/SocialPostEditorialCreate.vue')
        },
        {
          path: '/socialpostsedito/modeles/use/:id/:reference/:login',
          name: 'post-editorial-modeles-use',
          meta: {
            title: i18next.t('routes.socialPosts.models.use')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/socialNetworks/SocialPostEditorialCreate.vue')
        },
        {
          path: '/reviews',
          name: 'social-reviews',
          meta: {
            title: i18next.t('layouts.socialReviews')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/SocialReviews/SocialReviews.vue')
        },
        {
          path: '/reviews/auto-answers',
          name: 'social-reviews-auto-answers',
          meta: {
            title: i18next.t('layouts.socialReviews')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/SocialReviews/SocialReviewsAutoAnswers.vue')
        },
        {
          path: '/suggested-reviews',
          name: 'suggested-reviews',
          meta: {
            title: i18next.t('socialReviews.suggestedReviews')
          },
          component: () =>
            import(/* webpackChunkName: "posts" */ './pages/SocialReviews/SuggestedReviews.vue')
        },
        {
          path: '/produits',
          name: 'Produits',
          meta: {
            title: i18next.t('routes.ubiflowProducts')
          },
          beforeEnter: (to, from, next) => {
            if (i18next.language !== 'fr') {
              return next({ name: '404', params: { redirect: to.fullPath } })
            }
            return next()
          },
          component: () =>
            import(/* webpackChunkName: "produits" */ './pages/produits/Produits.vue')
        },
        {
          path: '/details_produit/:idProduit',
          name: 'details_produit',
          meta: {
            title: i18next.t('routes.ubiflowProducts')
          },
          component: () =>
            import(/* webpackChunkName: "details_produit" */ './pages/produits/DetailsProduit.vue')
        },
        {
          path: '/produit-qualiforma',
          name: 'ProduitQualiforma',
          meta: {
            title: i18next.t('routes.ubiflowProductQualiforma')
          },
          component: () =>
            import(/* webpackChunkName: "produit-qualiforma" */ './pages/produits/ProduitQualiforma.vue')
        },
        {
          path: '/nousContacter',
          name: 'nousContacter',
          meta: {
            title: i18next.t('routes.contactUsTitle')
          },
          component: () =>
            import(/* webpackChunkName: "nousContacter" */ './pages/Contact/NousContacter.vue')
        },
        {
          path: '/tutoralsVideos',
          name: 'tutoralsVideos',
          meta: {
            title: i18next.t('routes.tutoralsVideosTitle')
          },
          component: () =>
            import(/* webpackChunkName: "tutorialsVideos" */ './pages/tutorals/TutoralsVideos.vue')
        },
        {
          path: '/monSiteInternet',
          name: 'siteWeb',
          meta: {
            title: i18next.t('routes.myWebsiteTitle')
          },
          component: () =>
            import(/* webpackChunkName: "site" */ './pages/MonSiteWeb/SiteWeb.vue')
        },
        {
          path: '/integration-global-report',
          name: 'integration-global-report',
          meta: {
            title: i18next.t('routes.globalIntegrationReport')
          },
          component: () =>
            import(
              /* webpackChunkName: "portals" */ '@/pages/report/GlobalIntegrationReport.vue'
            )
        },
        {
          path: '/global-report',
          name: 'global-report',
          meta: {
            title: i18next.t('routes.globalDiffusionReport')
          },
          component: () =>
            import(
              /* webpackChunkName: "portals" */ '@/pages/report/GlobalDiffusionReport.vue'
            )
        },
        {
          path: '/statistics',
          name: 'statistics',
          meta: {
            title: i18next.t('routes.statistics')
          },
          component: () =>
            import(/* webpackChunkName: "statisticsMeta" */ '@/pages/statistics/index.vue')
        }
      ]
    },
    {
      path: '/login',
      name: 'login',
      component: () =>
        import(/* webpackChunkName: "login" */ './pages/Login.vue'),
      meta: {
        auth: false
      }
    },
    {
      path: '/motDePassePerdu',
      name: 'motDePassePerdu',
      component: () =>
        import(/* webpackChunkName: "motDePassePerdu" */ './pages/MotDePassePerdu.vue'),
      meta: {
        auth: false
      }
    },
    {
      path: '/lost-password-sfc',
      name: 'lostPasswordSFC',
      component: () =>
        import(/* webpackChunkName: "lostPasswordSFC" */ './pages/LostPasswordSFC.vue'),
      meta: {
        auth: false
      }
    },
    {
      path: '/sfc-connection-error',
      name: 'SFCConnectionError',
      component: () =>
        import(/* webpackChunkName: "SFCConnectionError" */ './pages/SFCConnectionError.vue'),
      meta: {
        auth: false
      }
    },
    {
      path: '/change-password',
      name: 'changePassword',
      component: () =>
        import(/* webpackChunkName: "motDePassePerdu" */ './pages/ChangePassword.vue'),
      meta: {
        auth: false
      }
    },
    {
      path: '/about',
      name: 'about',
      component: () =>
        import(/* webpackChunkName: "about" */ './pages/About.vue')
    },
    {
      path: '/activate-product',
      name: 'activateProduct',
      component: () =>
        import(/* webpackChunkName: "activateProduct" */ './pages/ActivateProduct.vue')
    },
    { path: '/401', name: '401', component: Unauthorized401 },
    { path: '/403', name: '403', component: Forbidden403 },
    { path: '*', name: '404', component: PageNotFound404 }
  ],
  scrollBehavior (to, from, savedPosition) {
    return { x: 0, y: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  // Bypass if its auto login with token
  if (to.name === 'login' && (to.query.token || to.params.token || to.params.signout)) {
    return next()
  }

  // Redirect to dashboard if one route is only for guests
  if (to.matched.some(({ meta }) => meta && meta.auth === false)) {
    const isAuth = await isAuthenticated()
    if (isAuth) return next({ name: 'dashboard' })
  }

  // Check access and authorization for each route
  if (to.matched.some(({ meta }) => meta && meta.auth)) {
    const isAuth = await isAuthenticated()
    if (!isAuth) {
      if (to.name === 'dashboard') {
        return next({
          name: 'login',
          params: { redirect: to.fullPath }
        })
      }

      return next({
        name: 'dashboard',
        params: { redirect: to.fullPath }
      })
    }

    const user = store.getters.getUser
    const hasAccess = await Promise.all(
      to.matched.map(async ({ meta }) => {
        if (!meta || !meta.auth || typeof meta.auth === 'boolean') return true
        if (!user) signOut()
        const permissions = Array.isArray(meta.auth) ? meta.auth : [meta.auth]
        return hasPermissions(user, permissions)
      })
    )
    if (hasAccess.indexOf(false) !== -1) {
      return next({ name: '403', params: { redirect: to.fullPath } })
    }
  }
  return next()
})

const originalPush = Router.prototype.push
Router.prototype.push = function push (location) {
  return originalPush.call(this, location).catch(err => err)
}

export default router
